/**
 * Silent Print Service - Browser-based printing for thermal receipts.
 *
 * Uses CSS @media print to show ONLY the receipt.
 * Supports multiple languages (ES, PT, EN) based on system configuration.
 */

import { getReceiptTranslations, getSourceLabel, getCurrentLanguage } from './receiptTranslations';
import { getItemComponents } from './receiptComponents';
import { API_URL } from './apiConfig';

const PRINT_CONTAINER_ID = "kaiso-print-receipt";

/** Inject the global print-only stylesheet once. */
let styleInjected = false;
function ensurePrintStyle() {
  if (styleInjected) return;
  const style = document.createElement("style");
  style.id = "kaiso-print-style";
  style.textContent = `
    @media print {
      /* Hide the entire app */
      body > *:not(#${PRINT_CONTAINER_ID}) { display: none !important; visibility: hidden !important; }
      /* Show only the receipt container */
      #${PRINT_CONTAINER_ID} {
        display: block !important;
        visibility: visible !important;
        position: absolute !important;
        top: 0 !important;
        left: 0 !important;
        width: 80mm !important;
        max-width: 80mm !important;
        margin: 0 !important;
        padding: 0 !important;
        z-index: 999999 !important;
        background: #fff !important;
        color: #000 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
      #${PRINT_CONTAINER_ID} * {
        visibility: visible !important;
        color: #000 !important;
        background: #fff !important;
      }
      @page {
        size: 80mm auto;
        margin: 0mm;
      }
      /* Remove headers/footers in print */
      html { margin: 0; }
    }
  `;
  document.head.appendChild(style);
  styleInjected = true;
}

/**
 * Print a receipt using in-page CSS @media print (no popup).
 * This is the preferred method for silent printing when QZ Tray is not available.
 * @param {Object} order
 * @param {Object} options
 * @returns {Promise<{success: boolean, method: string, error?: string}>}
 */
export function printReceiptSilent(order, options = {}) {
  return new Promise((resolve) => {
    try {
      const html = buildReceiptHTML(order, options);
      
      // Use in-page print method (no popup) - more reliable and silent
      ensurePrintStyle();
      const old = document.getElementById(PRINT_CONTAINER_ID);
      if (old) old.remove();
      
      const container = document.createElement("div");
      container.id = PRINT_CONTAINER_ID;
      container.style.cssText = "display:none;position:fixed;top:0;left:0;z-index:999999;";
      container.innerHTML = html;
      document.body.appendChild(container);
      container.style.display = "block";
      
      requestAnimationFrame(() => {
        setTimeout(() => {
          window.print();
          setTimeout(() => { 
            container.style.display = "none"; 
            container.remove(); 
          }, 500);
          resolve({ success: true, method: "browser_inpage" });
        }, 100);
      });
    } catch (err) {
      console.error("[PRINT] Error:", err);
      resolve({ success: false, method: "browser", error: err.message });
    }
  });
}

/**
 * Download receipt as standalone HTML file (ultimate fallback).
 * User can open the file and print from any browser/OS.
 */
export function downloadReceiptHTML(order, options = {}) {
  const html = buildReceiptHTML(order, options);
  const fullHTML = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Recibo #${(order.id || "").slice(-6)}</title><style>
    @page { size: 80mm auto; margin: 0; }
    body { margin: 0; padding: 0; font-family: 'Courier New', monospace; }
    @media screen { body { max-width: 80mm; margin: 20px auto; border: 1px dashed #ccc; } }
  </style></head><body onload="window.print()">${html}</body></html>`;
  const blob = new Blob([fullHTML], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `recibo-${(order.id || "teste").slice(-6)}.html`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return { success: true, method: "download" };
}

/**
 * Build professional receipt HTML for 80mm thermal printers.
 * Uses translations based on system language.
 */
function buildReceiptHTML(order, options = {}) {
  // Get language from options or system
  const lang = options.language || getCurrentLanguage();
  const t = getReceiptTranslations(lang);
  
  // Restaurant branding from options
  const storeName = options.storeName || "";
  const address = options.address || "";
  const phone = options.phone || "";
  const cif = options.showCif && options.cif ? options.cif : "";
  const footer = options.receiptFooter || t.thankYou;
  const logoUrl = options.logoUrl || "";
  const logoSrc = logoUrl ? (logoUrl.startsWith("http") ? logoUrl : `${API_URL || ""}${logoUrl}`) : "";
  const copies = options.copies || 1;
  const currency = options.currency || "eur";
  const currencySymbol = { eur: "\u20AC", usd: "$", brl: "R$", gbp: "\u00A3" }[currency] || "\u20AC";
  
  // QR Code config
  const menuUrl = options.menuUrl || "";
  const showQRCode = options.showQRCode !== false && menuUrl;
  const qrMessage = options.qrMessage || t.scanQR;
  
  // Social links
  const instagram = options.instagram || "";
  const whatsappPhone = options.whatsappPhone || "";
  
  const items = order.items || [];
  const date = order.created_at
    ? new Date(order.created_at).toLocaleString(lang === 'es' ? 'es-ES' : lang === 'pt' ? 'pt-BR' : 'en-US')
    : new Date().toLocaleString(lang === 'es' ? 'es-ES' : lang === 'pt' ? 'pt-BR' : 'en-US');

  const formatPrice = (value) => (value || 0).toFixed(2) + " " + currencySymbol;

  let itemsHTML = "";
  for (const item of items) {
    const qty = item.quantity || 1;
    const price = item.unit_price || item.price || 0;
    const total = qty * price;
    const name = item.product_name || item.name || "Item";
    itemsHTML += `
      <tr>
        <td style="padding:2px 0;font-size:11px;vertical-align:top;font-weight:bold;">${qty}x ${name}</td>
        <td style="padding:2px 0;font-size:11px;text-align:right;white-space:nowrap;">${formatPrice(total)}</td>
      </tr>`;
    // Modifiers (POS) or selected_variations (public menu) — unified
    for (const comp of getItemComponents(item)) {
      const compPrice = comp.price ? " +" + formatPrice(comp.price) : "";
      itemsHTML += `<tr><td colspan="2" style="font-size:10px;color:#444;padding-left:12px;">+ ${comp.label}${compPrice}</td></tr>`;
    }
    // Notes
    if (item.notes || item.observations || item.comment) {
      const note = item.notes || item.observations || item.comment;
      itemsHTML += `<tr><td colspan="2" style="font-size:10px;color:#444;padding-left:12px;">* ${note}</td></tr>`;
    }
  }

  // Calculate subtotal
  const subtotal = order.subtotal || items.reduce((s, i) => s + (i.quantity || 1) * (i.unit_price || i.price || 0), 0);
  
  let totalsHTML = "";
  totalsHTML += `<div style="display:flex;justify-content:space-between;font-size:11px;padding:1px 0;"><span>${t.subtotal}:</span><span>${formatPrice(subtotal)}</span></div>`;
  
  if (order.discount && order.discount > 0) {
    totalsHTML += `<div style="display:flex;justify-content:space-between;font-size:11px;padding:1px 0;"><span>${t.discount}:</span><span>-${formatPrice(order.discount)}</span></div>`;
  }
  if (order.tax && order.tax > 0) {
    totalsHTML += `<div style="display:flex;justify-content:space-between;font-size:11px;padding:1px 0;"><span>${t.tax}:</span><span>${formatPrice(order.tax)}</span></div>`;
  }
  if (order.delivery_fee && order.delivery_fee > 0) {
    totalsHTML += `<div style="display:flex;justify-content:space-between;font-size:11px;padding:1px 0;"><span>${t.deliveryFee}:</span><span>${formatPrice(order.delivery_fee)}</span></div>`;
  }
  if (order.service_charge && order.service_charge > 0) {
    totalsHTML += `<div style="display:flex;justify-content:space-between;font-size:11px;padding:1px 0;"><span>${t.serviceCharge}:</span><span>${formatPrice(order.service_charge)}</span></div>`;
  }

  // QR Code HTML (using a simple SVG-based QR or link)
  let qrHTML = "";
  if (showQRCode) {
    qrHTML = `
      <div style="text-align:center;margin:8px 0;">
        <div style="font-size:11px;color:#666;">--------------------------------</div>
        <div style="font-size:10px;margin:6px 0;">${qrMessage}</div>
        <div style="margin:6px 0;">
          <img src="https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(menuUrl)}" alt="QR Code" style="width:80px;height:80px;" />
        </div>
        <div style="font-size:9px;color:#666;word-break:break-all;">${menuUrl}</div>
      </div>`;
  }

  // Social links HTML
  let socialHTML = "";
  if (instagram) {
    const handle = instagram.includes("instagram.com/") ? "@" + instagram.split("instagram.com/")[1].replace(/\//g, "") : instagram;
    socialHTML += `<div style="font-size:10px;color:#444;">${handle}</div>`;
  }
  if (whatsappPhone) {
    socialHTML += `<div style="font-size:10px;color:#444;">WhatsApp: ${whatsappPhone}</div>`;
  }

  const source = order.source || order.channel;
  const sourceLabel = getSourceLabel(source, lang);
  
  let singleReceipt = `
    <div style="font-family:'Courier New',Courier,monospace;font-size:12px;width:80mm;color:#000;background:#fff;padding:4mm 2mm;">
      <!-- HEADER - Restaurant Identity -->
      ${logoSrc ? `<div style="text-align:center;margin-bottom:4px;"><img src="${logoSrc}" style="max-width:50mm;max-height:18mm;object-fit:contain;" /></div>` : ""}
      ${storeName ? `<div style="text-align:center;font-size:18px;font-weight:bold;letter-spacing:1px;">${storeName.toUpperCase()}</div>` : ""}
      ${address ? `<div style="text-align:center;font-size:10px;color:#333;">${address}</div>` : ""}
      ${phone ? `<div style="text-align:center;font-size:10px;color:#333;">${t.phone}: ${phone}</div>` : ""}
      ${cif ? `<div style="text-align:center;font-size:10px;color:#333;">${t.nif}: ${cif}</div>` : ""}
      
      <div style="text-align:center;font-size:11px;letter-spacing:-0.5px;margin:4px 0;">================================</div>
      
      <!-- ORDER INFO -->
      <div style="font-size:11px;line-height:1.6;margin:4px 0;">
        <div style="font-size:14px;font-weight:bold;">${t.orderNumber} #${(order.order_number || order.id || "").toString().slice(-6).toUpperCase()}</div>
        <div>${t.date}: ${date}</div>
        ${order.customer_name ? `<div>${t.customer}: ${order.customer_name}</div>` : ""}
        ${order.customer_phone ? `<div>${t.phone}: ${order.customer_phone}</div>` : ""}
        ${source ? `<div>${t.channel}: ${sourceLabel}</div>` : ""}
        ${order.table_number ? `<div>${t.table}: ${order.table_number}</div>` : ""}
        ${order.delivery_address ? `<div>${t.delivery}: ${order.delivery_address}</div>` : ""}
      </div>
      
      <div style="text-align:center;font-size:11px;letter-spacing:-0.5px;margin:4px 0;">================================</div>
      
      <!-- ITEMS -->
      <div style="text-align:center;font-weight:bold;font-size:12px;margin:4px 0;">${t.orderItems}</div>
      <div style="text-align:center;font-size:11px;color:#666;margin:2px 0;">--------------------------------</div>
      <table style="width:100%;border-collapse:collapse;">${itemsHTML}</table>
      <div style="text-align:center;font-size:11px;color:#666;margin:2px 0;">--------------------------------</div>
      
      <!-- TOTALS -->
      ${totalsHTML}
      
      <div style="text-align:center;font-size:11px;letter-spacing:-0.5px;margin:4px 0;">================================</div>
      
      <!-- TOTAL -->
      <div style="text-align:center;font-size:18px;font-weight:bold;margin:6px 0;">
        ${t.total} ${formatPrice(order.total)}
      </div>
      
      <div style="text-align:center;font-size:11px;letter-spacing:-0.5px;margin:4px 0;">================================</div>
      
      <!-- PAYMENT -->
      ${order.payment_method ? `<div style="font-size:11px;font-weight:bold;margin:2px 0;">${t.payment}: ${order.payment_method.toUpperCase()}</div>` : ""}
      ${order.change_amount && order.change_amount > 0 ? `<div style="font-size:11px;margin:2px 0;">${t.change}: ${formatPrice(order.change_amount)}</div>` : ""}
      
      <!-- ORDER NOTES -->
      ${order.notes || order.observations ? `
        <div style="margin:4px 0;">
          <span style="font-weight:bold;font-size:11px;">${t.notes}: </span>
          <span style="font-size:11px;">${order.notes || order.observations}</span>
        </div>` : ""}
      
      <!-- QR CODE -->
      ${qrHTML}
      
      <!-- FOOTER -->
      <div style="text-align:center;font-size:11px;letter-spacing:-0.5px;margin:4px 0;">================================</div>
      <div style="text-align:center;font-size:11px;margin-top:6px;color:#333;">${footer}</div>
      ${socialHTML ? `<div style="text-align:center;margin-top:4px;">${socialHTML}</div>` : ""}
      
      <div style="margin-top:10px;"></div>
    </div>`;

  let result = "";
  for (let i = 0; i < copies; i++) {
    if (i > 0) result += '<div style="page-break-after:always;"></div>';
    result += singleReceipt;
  }
  return result;
}

export { buildReceiptHTML };
export default { printReceiptSilent, downloadReceiptHTML, buildReceiptHTML };
